/**
 * Phase 11.5b — Evidence KG paths chip-chain visualizer.
 *
 * Renders `entry.evidence_kg_paths[]` as clickable node chips joined by
 * edge_type labels. Assumed path shape (defensive — accepts common
 * variants):
 *   path = [ {node_id, node_type, label, edge_type_out?}, ... ]
 * where `edge_type_out` is the edge leading to the *next* node in the
 * chain (last node has no `edge_type_out`).
 *
 * Falls back to `n.id` when `label` is missing, and to `"—"` when a
 * chip has no id at all.
 */
import { nodeColorClass } from "./KnowledgeGraphDrawer";
import { ArrowRight } from "lucide-react";

const COPY = {
  de: {
    header: "KG-Pfade",
    open_in_kg: "Im KG öffnen",
  },
  en: {
    header: "KG paths",
    open_in_kg: "Open in KG",
  },
};

function truncate(s, n = 20) {
  if (!s) return "—";
  return s.length > n ? s.slice(0, n - 1) + "…" : s;
}

function NodeChip({ node, onOpen, testId }) {
  const label = truncate(node.label || node.node_id || "—");
  const cls = nodeColorClass(node.node_type);
  return (
    <button
      type="button"
      data-testid={testId}
      data-node-id={node.node_id || ""}
      onClick={() => node.node_id && onOpen && onOpen(node.node_id)}
      title={node.label || node.node_id || ""}
      className={
        "inline-flex items-center gap-1 rounded-full px-2.5 py-1 " +
        "text-[11px] font-medium border transition-shadow " +
        "hover:shadow-sm focus:outline-none focus:ring-2 " +
        "focus:ring-offset-1 focus:ring-slate-400 " + cls
      }
    >
      <span className="opacity-70">{node.node_type || "Node"}</span>
      <span className="mx-1 opacity-40">·</span>
      <span>{label}</span>
    </button>
  );
}

export default function EvidenceKgPaths({ paths, locale = "de", onOpenNode }) {
  const t = COPY[locale] || COPY.en;
  const list = Array.isArray(paths) ? paths.filter((p) => Array.isArray(p) && p.length > 0) : [];
  // Hide entire section (including header) when nothing to show.
  if (list.length === 0) return null;
  return (
    <div data-testid="sm-drawer-kg-paths-viz">
      <div className="text-xs uppercase text-muted-foreground mb-2">
        {t.header}
      </div>
      <ul className="space-y-2" data-testid="sm-drawer-kg-paths-list">
        {list.map((path, pIdx) => (
          <li
            key={pIdx}
            data-testid={`sm-drawer-kg-path-${pIdx}`}
            className="flex flex-wrap items-center gap-1 rounded-md border border-dashed p-2"
          >
            {path.map((node, nIdx) => (
              <span
                key={nIdx}
                className="inline-flex items-center gap-1"
              >
                <NodeChip
                  node={node}
                  onOpen={onOpenNode}
                  testId={`sm-drawer-kg-path-${pIdx}-chip-${nIdx}`}
                />
                {nIdx < path.length - 1 && (
                  <span
                    data-testid={`sm-drawer-kg-path-${pIdx}-edge-${nIdx}`}
                    className="inline-flex items-center gap-1 text-[10px] font-mono uppercase text-muted-foreground"
                  >
                    <ArrowRight className="size-3" />
                    {node.edge_type_out || node.edge_type || ""}
                    <ArrowRight className="size-3 opacity-40" />
                  </span>
                )}
              </span>
            ))}
          </li>
        ))}
      </ul>
    </div>
  );
}
